/**
 * HttpClient 请求类
 * 扩展功能
 * 1、提供多个请求同时触发并且可能短时间内出现前后请求依赖的统一管理方法
 * 2、接口缓存和读取缓存（后期有需求再扩展）
 * 3、同时等待多个请求完毕统一处理
 * 4、同时多个请求关系竞争结果处理
 * 注意点
 * 1、请求url为运行的唯一标识，若存在相同接口短时间多次请求需添加标识，比如url+时间戳
 */
import axios from "axios";

class HttpClient {
  /**
   * @description 构造函数
   * @param  {object} config axios配置
   * @param  {func} runLoad 运行load方法
   * @param  {func} stopLoad 关闭load方法
   */
  constructor(config, operate) {
    const { requestStart, requestEnd } = operate;
    this.requestQueue = []; // 请求队列，此处存储请求地址
    this.requestQueueConfig = []; // 存储与requestQueue顺序对应的请求config
    this.requestStart = requestStart; // 统一请求加载前
    this.requestEnd = requestEnd; // 统一请求结束后
    this.load = false; // 是否请求中
    this.axiosInstance = axios.create(config); // 实例axios

    // 重写内部 axios 实例的响应拦截对象 use 方法
    let resUse = this.axiosInstance.interceptors.response.use;
    this.axiosInstance.interceptors.response.use = (
      interceptor,
      errorHandler
    ) => {
      resUse.call(
        this.axiosInstance.interceptors.response,
        response => {
          this.del(response.config.simpleUrl);
          this.stopLoad(response.config);
          return interceptor(response);
        },
        error => {
          // 拦截请求，返回缓存数据
          if (axios.isCancel(error)) {
            return Promise.resolve(error.message);
          }
          return errorHandler(error);
        }
      );
    };

    // 重写内部 axios 实例的请求拦截对象 use 方法
    let reqUse = this.axiosInstance.interceptors.request.use;
    this.axiosInstance.interceptors.request.use = (
      interceptor,
      errorHandler
    ) => {
      reqUse.call(
        this.axiosInstance.interceptors.request,
        config => {
          this.push(config.simpleUrl, config); // 请求加入队列
          this.runLoad(config);
          return interceptor(config);
        },
        errorHandler
      );
    };

    this.axiosInstance.interceptors.request.use(config => {
      return config;
    });
  }

  del = url => {
    const num = this.requestQueue.indexOf(url);
    this.requestQueue.splice(num, 1);
    this.requestQueueConfig.splice(num, 1);
  };

  push = (url, config) => {
    const num = this.requestQueue.indexOf(url);
    if (num === -1) {
      this.requestQueue.push(url);
      this.requestQueueConfig.push(config);
    } else {
      this.requestQueueConfig.splice(num, 1, config);
    }
  };

  size = () => {
    return this.requestQueue.length;
  };

  runLoad = config => {
    if (this.size() !== 0 && !this.load) {
      this.load = true;
      this.requestStart(config);
    }
  };

  stopLoad = config => {
    if (this.size() === 0) {
      this.load = false;
      this.requestEnd(config);
    }
  };

  /**
   * @description 请求
   * @param  {string} method
   * @param  {string} url
   * @param  {object} {data
   * @param  {object} config={}
   * @param  {bool} loadPopup}
   * @returns 返回axios请求实例
   */
  createHttp = (method, url, { data, config = {}, loadPopup = true }) => {
    let CancelToken = axios.CancelToken;
    let cancel;
    let token = new CancelToken(_cancel => {
      cancel = _cancel;
    });
    if (data) {
      return this.axiosInstance[method](url, data, {
        ...config,
        cancelToken: token,
        cancel,
        loadPopup,
        simpleUrl: url
      });
    } else {
      return this.axiosInstance[method](url, {
        ...config,
        cancelToken: token,
        cancel,
        loadPopup,
        simpleUrl: url
      });
    }
  };

  /**
   * @description 顺序操作请求
   */
  createSequenceHttps = (https = [], runFunc) => {
    this.requestQueue = https.map(item => item);
    this.requestQueueConfig = https.map(item => ({
      url: item
    }));
    runFunc();
  };
}

export default HttpClient;
